import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import {
  ClientRegisteredVendor,
  CompanyVendor,
  CompanyVendorFile,
  ExemptVendor,
  SignedDocument,
  User,
  Vendor
} from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { CompanyVendorFileUploadModalComponent } from 'src/app/modals/company-vendor-file-upload-modal/company-vendor-file-upload-modal.component';
import { SignedDocumentFileDownloadModalComponent } from 'src/app/modals/signed-document-file-download-modal/signed-document-file-download-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { FileService } from 'src/app/services/file.service';
import { SignedDocumentService } from 'src/app/services/signed-document.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-client-my-vendors-documents',
  templateUrl: './client-my-vendor-documents.component.html',
  styleUrls: ['./client-my-vendor-documents.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorDocumentsComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private FileService: FileService,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private FormBuilder: FormBuilder,
    private AuthService: AuthService,
    private ModalService: NgbModal,
    private SignedDocumentService: SignedDocumentService
  ) {
    this.LoggedInUser = this.AuthService.getUser();

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role) {
      this.hasAcceptedSignedDocumentRole = new Set([
        'ADMIN_ADMIN',
        'CLIENT_ADMIN'
      ]).has(role);
    }

    this.SignedDocumentActionsForm.get('showArchived')?.valueChanges.subscribe({
      next: (changes) => {
        if (changes) {
          Object.assign(this.SignedDocumentsSuppliedGridQueryData, {
            isArchived: { $in: [true, false] }
          });
        } else {
          Object.assign(this.SignedDocumentsSuppliedGridQueryData, {
            isArchived: false
          });
        }

        this.reloadSignedDocumentsGrid = true;
        this._cdr.markForCheck();
      }
    });

    this.CompanyVendorFileActionForm.get(
      'showArchived'
    )?.valueChanges.subscribe({
      next: (changes) => {
        if (changes) {
          Object.assign(this.CompanyVendorFilesSuppliedGridQueryData, {
            isArchived: { $in: [true, false] }
          });
        } else {
          Object.assign(this.CompanyVendorFilesSuppliedGridQueryData, {
            isArchived: false
          });
        }

        this.reloadCompanyVendorFileGrid = true;
        this._cdr.markForCheck();
      }
    });
  }

  Vendor: Vendor | ExemptVendor | ClientRegisteredVendor | null = null;
  CompanyVendor?: CompanyVendor;
  LoggedInUser: User;
  hasAcceptedSignedDocumentRole: boolean = false;

  VendorId?: number;
  ExemptVendorId?: number;
  ClientRegisteredVendorId?: number;

  SignedDocumentActionsForm: FormGroup = this.FormBuilder.group({
    showArchived: [false]
  });

  CompanyVendorFileActionForm: FormGroup = this.FormBuilder.group({
    showArchived: [false]
  });

  CompanyVendorFilesSuppliedGridQueryData: GenericObject = {
    attributes: ['id'],
    isArchived: false,
    // Root aliased columns — correlated literals on CompanyVendorFile (not
    // InsuranceCertificate join aliases), so sort/filter work with pagination.
    aliasedAttributes: ['policyTypes', 'nextExpiringInsurancePolicyDate']
  };

  SignedDocumentsSuppliedGridQueryData: GenericObject = {
    attributes: ['id', 'documentId'],
    isArchived: false,
    $or: [
      {
        service: { $ne: 'RSign' }
      },
      {
        $and: [{ service: 'RSign' }, { signingUrl: { $ne: null } }]
      }
    ]
  };

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['download']
  };

  reloadSignedDocumentsGrid: boolean = false;
  reloadCompanyVendorFileGrid: boolean = false;

  addCompanyVendorFile() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor information.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      CompanyVendorFileUploadModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    const {
      id,
      CompanyId,
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId
    } = this.CompanyVendor;
    const CompanyVendor = {
      id,
      CompanyId,
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId
    };

    if (!CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    const totalVendorIds = [
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId
    ].reduce((accum, currentValue) => {
      if (currentValue) {
        return accum! + 1;
      }

      return accum;
    }, 0);

    if (!totalVendorIds) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (totalVendorIds > 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Multiple Vendor Ids found.',
        { returnError: true }
      );
    }

    Object.assign(componentInstance, {
      title: 'Add Document',
      CompanyVendor
    });

    from(result).subscribe({
      next: (CompanyVendorFiles) => {
        this.FileService.saveCompanyVendorFiles(CompanyVendorFiles, {
          CompanyId,
          VendorId,
          ExemptVendorId,
          ClientRegisteredVendorId
        }).subscribe({
          next: () => {
            this.reloadCompanyVendorFileGrid = true;
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully saved Vendor document(s).'
            );
            this._cdr.markForCheck();
          }
        });
      }
    });
  }

  downloadFile(companyVendorFileId: number) {
    const CompanyVendorFileQuery: GenericObject = {
      id: companyVendorFileId,
      File: {}
    };

    if (this.VendorId) {
      Object.assign(CompanyVendorFileQuery, { VendorId: this.VendorId });
    } else if (this.ExemptVendorId) {
      Object.assign(CompanyVendorFileQuery, {
        ExemptVendorId: this.ExemptVendorId
      });
    } else if (this.ClientRegisteredVendorId) {
      Object.assign(CompanyVendorFileQuery, {
        ClientRegisteredVendorId: this.ClientRegisteredVendorId
      });
    }

    this.FileService.getCompanyVendorFile(
      this.UtilityService.stringifyObjectsInQueryObject(CompanyVendorFileQuery)
    ).subscribe({
      next: (companyVendorFile: CompanyVendorFile) => {
        if (!companyVendorFile) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No CompanyVendor File found to download.',
            { returnError: true }
          );
        }

        const { File } = companyVendorFile;

        if (!File) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No File found to download.',
            { returnError: true }
          );
        }

        const { filepath } = File;

        if (filepath) {
          this.FileService.getCompanyVendorFileS3PresignedUrl({
            id: companyVendorFileId
          }).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        } else {
          this.UtilityService.downloadFile(File);
        }
      }
    });
  }

  companyVendorFileAction({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: CompanyVendorFile;
  }) {
    const { id } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download file. Missing ID.',
        { returnError: true }
      );
    }

    if (action === 'download') {
      this.downloadFile(id);
    }
  }

  signedDocumentsAction({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: SignedDocument;
  }) {
    const { id, service } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download file. Missing ID.',
        { returnError: true }
      );
    }

    if (action === 'download') {
      const { componentInstance } = this.ModalService.open(
        SignedDocumentFileDownloadModalComponent,
        { backdrop: 'static', size: 'md' }
      );
      Object.assign(componentInstance, { SignedDocumentId: id });
    }
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId'));
    const ExemptVendorId = Number(paramMap.get('ExemptVendorId'));
    const ClientRegisteredVendorId = Number(
      paramMap.get('ClientRegisteredVendorId')
    );
    const CompanyId = Number(paramMap.get('CompanyId'));
    const companyVendorQuery = {};

    const countOfVendorIds = [
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId
    ].reduce((accum, currentValue) => {
      if (currentValue) {
        return accum + 1;
      }

      return accum;
    }, 0);

    if (countOfVendorIds === 0 || countOfVendorIds > 1) {
      throw this.ToastService.addErrorToast('Error', 'Vendor id is invalid.', {
        returnError: true
      });
    }

    const vendorQuery = {
      attributes: ['legalName']
    };

    if (VendorId) {
      Object.assign(companyVendorQuery, {
        VendorId,
        Vendor: vendorQuery
      });

      Object.assign(this.SignedDocumentsSuppliedGridQueryData, {
        VendorId,
        CompanyId
      });
      this.VendorId = VendorId;
    }

    if (ExemptVendorId) {
      Object.assign(companyVendorQuery, {
        ExemptVendorId,
        ExemptVendor: vendorQuery
      });
      this.ExemptVendorId = ExemptVendorId;
    }

    if (ClientRegisteredVendorId) {
      Object.assign(companyVendorQuery, {
        ClientRegisteredVendorId,
        ClientRegisteredVendor: vendorQuery
      });
      this.ClientRegisteredVendorId = ClientRegisteredVendorId;
    }

    this.CompanyVendorService.getCompanyVendor(
      this.UtilityService.stringifyObjectsInQueryObject(companyVendorQuery)
    ).subscribe({
      next: (CompanyVendor: CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No CompanyVendor Record found.',
            { returnError: true }
          );
        }

        this.CompanyVendor = CompanyVendor;

        const {
          id: CompanyVendorId,
          Vendor,
          ExemptVendor,
          ClientRegisteredVendor
        } = this.CompanyVendor;

        Object.assign(this.CompanyVendorFilesSuppliedGridQueryData, {
          CompanyVendorId
        });

        if (Vendor) {
          this.Vendor = Vendor;
        }

        if (ExemptVendor) {
          this.Vendor = ExemptVendor;
        }

        if (ClientRegisteredVendor) {
          this.Vendor = ClientRegisteredVendor;
        }

        if (!this.Vendor) {
          throw this.ToastService.addErrorToast('Error', 'No Vendor Found.', {
            returnError: true
          });
        }
        this._cdr.markForCheck();
      }
    });
  }
}
